import React, { useState, useEffect, useRef } from 'react';
import { Spinner, Modal, Button, ProgressBar, Form } from 'react-bootstrap';
import { showToast as toast } from '../toastManager.js';
import errorMessages from '../errorMessages.js';
import Cookies from 'js-cookie';
import axios from 'axios';

function SavedSections({ ViewedPage, shop_domain, app_url }) {
    ViewedPage(33);

    const [loading, setLoading] = useState(false);
    const [approvedByLp, setApprovedByLp] = useState({});
    const [landingPages, setLandingPages] = useState([]);
    const [selectedLp, setSelectedLp] = useState('');

    // Progress modal state for Unpublish
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [jobStatus, setJobStatus] = useState('');
    const [jobProgress, setJobProgress] = useState([]);
    const [progressTitle, setProgressTitle] = useState('Processing...');
    const [conflicts, setConflicts] = useState([]);                  // 409 conflict rows shown in the progress modal
    const pollIntervalRef = useRef(null);
    const isMountedRef = useRef(true);
    const fetchAbortRef = useRef(null);

    // Unpublish (unsave) picker modal state
    const [showUnsaveModal, setShowUnsaveModal] = useState(false);
    const [unsaveSection, setUnsaveSection] = useState(null);          // { section_id, section_name, cardLp }
    const [unsaveLpOptions, setUnsaveLpOptions] = useState([]);        // LPs the section is approved on
    const [unsaveSelectedLp, setUnsaveSelectedLp] = useState('');
    const [unsaveBusy, setUnsaveBusy] = useState(false);
    const unsaveContainerRef = useRef(null);                          // clicked card's DOM node (for removal)
    const sectionLpsMapRef = useRef({});                              // section_id -> [landing_page, ...]


    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
            if (fetchAbortRef.current) {
                try { fetchAbortRef.current.abort(); } catch (_) {}
                fetchAbortRef.current = null;
            }
        };
    }, []);

    // Build a section_id -> [landing_page, ...] map from the UNFILTERED approved
    // sections, so the Unpublish picker can list every LP a section is on (the
    // main list fetch below is filtered by selectedLp and can't see the others).
    useEffect(() => {
        if (!app_url) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.post(app_url + "/api/firebase/get_approved_sections", {}, { withCredentials: true });
                const grouped = res?.data?.approved_sections || {};
                const map = {};
                Object.entries(grouped).forEach(([lp, arr]) => {
                    (Array.isArray(arr) ? arr : []).forEach(sec => {
                        if (!sec?.section_id) return;
                        if (!map[sec.section_id]) map[sec.section_id] = [];
                        if (!map[sec.section_id].includes(lp)) map[sec.section_id].push(lp);
                    });
                });
                if (!cancelled) sectionLpsMapRef.current = map;
            } catch (e) {
                console.error('Error building section→LP map:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [app_url, shop_domain]);

    useEffect(() => {
        setLoading(true);
        const fetchApproved = async () => {
            try {
                // Fetch landing pages for filter
                const lpRes = await axios.post(app_url + "/api/firebase/get_landing_pages", {}, { withCredentials: true });
                const lpList = Array.isArray(lpRes?.data?.landing_pages) ? lpRes.data.landing_pages : [];
                setLandingPages(lpList);

                // Fetch approved sections (optionally filter by selectedLp)
                const body = selectedLp ? { landing_page: selectedLp } : {};
                const res = await axios.post(app_url + "/api/firebase/get_approved_sections", body, { withCredentials: true });
                const grouped = res?.data?.approved_sections || {};
                setApprovedByLp(grouped);

                // Build a flat list for HTML fetching
                const sections = Object.entries(grouped).flatMap(([lp, arr]) => (Array.isArray(arr) ? arr.map(e => ({ ...e, _lp: lp })) : []));
                await fetchHtmlContent(sections);
            } catch (err) {
                console.error('Error fetching approved sections:', err);
                toast.error('Failed to load approved sections');
            } finally {
                setLoading(false);
            }
        };

        // Function to fetch HTML content from URLs
        const fetchHtmlContent = async (sections) => {
            try {
                // Abort any previous in-flight rendering
                if (fetchAbortRef.current) {
                    try { fetchAbortRef.current.abort(); } catch (_) {}
                }
                const controller = new AbortController();
                fetchAbortRef.current = controller;
                const htmlPromises = sections.map(async (section) => {
                    try {
                        // Use backend endpoint to avoid CORS issues
                        const response = await axios.post(app_url + "/api/firebase/get_section_html", {
                            url: section.file_url
                        }, { withCredentials: true, signal: controller.signal });

                        if (response.status !== 200) {
                            throw new Error(`HTTP error! status: ${response.status}`);
                        }

                        const html = response.data.data;
                        return {
                            ...section,
                            htmlContent: html
                        };
                    } catch (error) {
                        if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED' || (typeof error?.message === 'string' && error.message.includes('canceled'))) {
                            return null; // silently skip aborted results
                        }
                        console.error(`Error fetching HTML from ${section.file_url}:`, error);
                        return {
                            ...section,
                            htmlContent: `<div style=\"padding:12px;color:#721c24;\">Error loading content</div>`
                        };
                    }
                });

                const htmlResultsRaw = await Promise.all(htmlPromises);
                const htmlResults = htmlResultsRaw.filter(Boolean);

                let saved_div_preview = document.getElementById("saved_div_preview");

                // Clear previous results to reflect current filter selection
                if (saved_div_preview) {
                    saved_div_preview.innerHTML = '';
                }

                htmlResults.forEach(result => {
                    if (!isMountedRef.current) return;
                    if (!saved_div_preview) return;
                    if (saved_div_preview.querySelector(`#${result.section_id}`)) return;
                
                    // Create section container
                    let sectionContainer = document.createElement("div");
                    sectionContainer.id = `container_${result.section_id}`;
                    sectionContainer.style.marginBottom = "2rem";

                    // Create a wrapper div for the iframe
                    let wrapperDiv = document.createElement("div");
                    wrapperDiv.id = `wrapper_${result.section_id}`;
                    wrapperDiv.style.position = "relative";
                    wrapperDiv.style.cursor = "default";
                    wrapperDiv.style.border = "1px solid #e9ecef";
                    wrapperDiv.style.borderRadius = "8px";
                    wrapperDiv.style.transition = "all 0.3s ease";
                    wrapperDiv.style.padding = "10px";
                    wrapperDiv.style.backgroundColor = "#ffffff";
                    wrapperDiv.title = "";
                    wrapperDiv.className = "sxi-saved-div-wrapper";
                    // Allow interactions to pass to iframe; buttons will remain clickable
                    wrapperDiv.style.pointerEvents = "none";
                    wrapperDiv.style.zIndex = "0";

                     // Add hover effect
                    wrapperDiv.addEventListener('mouseenter', () => {
                        wrapperDiv.style.border = "1px solid #007bff";
                        wrapperDiv.style.backgroundColor = "#f8f9fa";
                    });
                    
                    wrapperDiv.addEventListener('mouseleave', () => {
                        wrapperDiv.style.border = "1px solid #e9ecef";
                        wrapperDiv.style.backgroundColor = "#ffffff";
                    });
                    
                    // Delete/Unpublish button positioned at top right corner
                    let unsaveButton = document.createElement("button");
                    unsaveButton.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style="margin-right: 6px;"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>Delete`;
                    unsaveButton.style.position = "absolute";
                    unsaveButton.style.top = "12px";
                    unsaveButton.style.right = "12px";
                    unsaveButton.style.display = "flex";
                    unsaveButton.style.alignItems = "center";
                    unsaveButton.style.fontSize = "14px";
                    unsaveButton.style.padding = "8px 16px";
                    unsaveButton.style.borderRadius = "6px";
                    unsaveButton.style.fontWeight = "500";
                    unsaveButton.style.color = "#212529";
                    unsaveButton.style.border = "1px solid #dee2e6";
                    unsaveButton.style.cursor = "pointer";
                    unsaveButton.style.backgroundColor = "#ffffff";
                    unsaveButton.style.whiteSpace = "nowrap";
                    unsaveButton.style.transition = "all 0.2s ease";
                    unsaveButton.style.zIndex = "10";
                    unsaveButton.style.pointerEvents = "auto";
                    unsaveButton.addEventListener('mouseenter', () => {
                        unsaveButton.style.backgroundColor = "#f8f9fa";
                    });
                    unsaveButton.addEventListener('mouseleave', () => {
                        unsaveButton.style.backgroundColor = "#ffffff";
                    });
                    unsaveButton.addEventListener('click', (e) => {
                        e.stopPropagation();
                        openUnsaveModal(result, sectionContainer);
                    });

                    let iframe = document.createElement("iframe");
                    iframe.id = result.section_id;
                    iframe.style.width = "100%";
                    iframe.style.height = "30rem";
                    iframe.className = "sxi-saved-div-preview";
                    iframe.style.pointerEvents = "auto"; // Allow scrolling and interactions
                    iframe.style.touchAction = "auto";
                    iframe.style.position = "relative";
                    iframe.style.zIndex = "1";
                    
                
                    // Append elements (guard against race conditions if container unmounted)
                    if (!isMountedRef.current) return;
                    if (!saved_div_preview || !saved_div_preview.isConnected) return;
                    saved_div_preview.appendChild(sectionContainer);
                    sectionContainer.appendChild(wrapperDiv);
                    if (!wrapperDiv) return;
                    wrapperDiv.appendChild(unsaveButton); // Add button first (top right)
                    wrapperDiv.appendChild(iframe);
                
                
                    // Now inject the HTML into the iframe
                    if (!isMountedRef.current || !sectionContainer.isConnected) return;
                    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                    iframeDoc.open();
                    iframeDoc.write(result.htmlContent);
                    iframeDoc.close();
                });

            } catch (error) {
                console.error('Error fetching HTML content:', error);
                toast.error('Failed to load HTML content');
            }
        };
        fetchApproved();
    }, [app_url, shop_domain, selectedLp])

    // Open the Unpublish picker. `container` is the card's DOM node so we can
    // remove it on success. Options = every LP this section is approved on.
    const openUnsaveModal = (section, container) => {
        unsaveContainerRef.current = container || null;
        const lps = sectionLpsMapRef.current[section.section_id];
        let options = (Array.isArray(lps) && lps.length > 0) ? [...lps] : (section._lp ? [section._lp] : []);
        if (section._lp && !options.includes(section._lp)) options.unshift(section._lp);
        setUnsaveSection({ section_id: section.section_id, section_name: section.section_name, cardLp: section._lp });
        setUnsaveLpOptions(options);
        setUnsaveSelectedLp(section._lp || options[0] || '');
        setShowUnsaveModal(true);
    };

    // Submit the unsave for the chosen LP. Mirrors the old inline flow (202 + poll),
    // adds a 409 branch that names the campaign/variant blocking the unsave, and
    // only removes the visible card when we removed the LP that card represents.
    const submitUnsave = async () => {
        if (!unsaveSection || !unsaveSelectedLp) return;
        const removedLp = unsaveSelectedLp;
        const removedSectionId = unsaveSection.section_id;
        const cardLp = unsaveSection.cardLp;
        const container = unsaveContainerRef.current;
        const dropLpFromMap = () => {
            const remaining = (sectionLpsMapRef.current[removedSectionId] || []).filter(lp => lp !== removedLp);
            sectionLpsMapRef.current[removedSectionId] = remaining;
        };

        setUnsaveBusy(true);
        try {
            const response = await axios.post(app_url + "/api/firebase/unsave_section", {
                section_ids: [removedSectionId],
                landing_page: removedLp
            }, { withCredentials: true });

            // Async path (202 + jobId): close picker, show progress, poll to completion
            if (response.status === 202 && response.data?.jobId) {
                const jobId = response.data.jobId;
                setShowUnsaveModal(false);
                setConflicts([]);
                setProgressTitle('Unpublishing section');
                setJobStatus('queued');
                setJobProgress([{ ts: new Date().toISOString(), message: 'Queued' }]);
                setShowProgressModal(true);

                const intervalMs = 1500;
                const maxMinutes = 10;
                const maxTicks = Math.ceil((maxMinutes * 60 * 1000) / intervalMs);
                let ticks = 0;
                if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
                pollIntervalRef.current = setInterval(async () => {
                    try {
                        ticks += 1;
                        const statusResp = await axios.get(app_url + `/api/firebase/job_status/${jobId}`, { withCredentials: true });
                        const job = statusResp?.data?.job || {};
                        setJobStatus(job.status || '');
                        setJobProgress(job.progress || []);
                        if (job.status === 'succeeded') {
                            clearInterval(pollIntervalRef.current); pollIntervalRef.current = null;
                            setShowProgressModal(false);
                            toast.success('Unpublished section');
                            dropLpFromMap();
                            if (removedLp === cardLp) container?.remove();
                        } else if (job.status === 'failed') {
                            clearInterval(pollIntervalRef.current); pollIntervalRef.current = null;
                            setShowProgressModal(false);
                            toast.error(job?.error?.message || 'Failed to unpublish section');
                        } else if (ticks >= maxTicks) {
                            clearInterval(pollIntervalRef.current); pollIntervalRef.current = null;
                            setShowProgressModal(false);
                            toast.error('Timed out while unpublishing. Please check later.');
                        }
                    } catch (err) {
                        clearInterval(pollIntervalRef.current); pollIntervalRef.current = null;
                        setShowProgressModal(false);
                        toast.error('Failed to get unpublish status');
                    }
                }, intervalMs);
                return;
            }

            // Legacy immediate path
            if (response.data.success) {
                const successCount = response?.data?.successCount ?? 1;
                const failCount = response?.data?.failCount ?? 0;
                if (successCount > 0) toast.success(`Unpublished ${successCount} section(s)`);
                if (failCount > 0) toast.error(`Failed to unpublish ${failCount} section(s)`);
                dropLpFromMap();
                if (removedLp === cardLp) container?.remove();
                setShowUnsaveModal(false);
            } else {
                toast.error(response.data.message || "Failed to unpublish section");
            }
        } catch (error) {
            // 409 = a campaign variation on the chosen LP still uses the section.
            // Show the conflict as a branded table inside the progress modal.
            if (error.response?.status === 409) {
                setConflicts(error.response.data?.conflicts || []);
                setProgressTitle('Section in use');
                setShowUnsaveModal(false);
                setShowProgressModal(true);
            } else {
                console.error("Error unpublishing section:", error);
                toast.error("Failed to unpublish section");
            }
        } finally {
            setUnsaveBusy(false);
        }
    };

    // Flatten 409 conflicts into Campaign | Variant rows for the in-modal table.
    const conflictRows = conflicts.flatMap(c => (c.used_in || []).map(u => ({ ...u })));

    return (
        <div className='main-container'>
            {!shop_domain ? (
                <div className='go_to_shopify_admin_div'>
                    <h5>!! Go to Shopify Admin and open our app !!</h5>
                </div>
            ) : (
                <>
                    <div 
                        className='main-title mt-4 mx-4 d-flex justify-content-between align-items-center' 
                        style={{ 
                            marginBottom: '10px',
                            border: '1px solid #e9ecef',
                            borderRadius: '8px',
                            padding: '16px 20px',
                            backgroundColor: '#ffffff'
                        }}
                    >
                        <h2 style={{ margin: 0, fontWeight: 'bold' }}>Approved Sections</h2>
                        <div className='d-flex gap-2 align-items-center'>
                            <label htmlFor='lpFilter' style={{marginRight: '8px', marginBottom: 0, fontSize: '14px', color: '#212529'}}>Filter by landing page:</label>
                            <select 
                                id='lpFilter' 
                                className='form-select form-select-sm'
                                value={selectedLp} 
                                onChange={(e) => {
                                    e.preventDefault();
                                    console.log('selectedLp', e.target.value);
                                    setSelectedLp(e.target.value);
                                }}
                                style={{ 
                                    minWidth: '150px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    padding: '6px 12px',
                                    fontSize: '14px'
                                }}
                            >
                                <option value=''>All</option>
                                {landingPages.map(lp => (
                                    <option key={lp} value={lp}>{lp}</option>
                                ))}
                            </select>
                        </div>
                    </div>
 
                    {loading && (
                        <div className='main_conatiner_spinner'>
                            <Spinner animation='border' />
                        </div>
                    )}
                    <div id='saved_div_preview' className='mt-2 mx-3 p-2'>
                    </div>
                </>
            )}
            {/* Progress Modal */}
            <Modal show={showProgressModal} onHide={() => {
                if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                }
                setConflicts([]);
                setShowProgressModal(false);
            }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{progressTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {conflicts.length > 0 ? (
                        <div>
                            <p style={{ fontSize: '14px', marginBottom: '12px', color: '#344054' }}>
                                <strong>{unsaveSection?.section_name || unsaveSection?.section_id || 'This section'}</strong> is in use by the following campaign variation(s) on this landing page. Remove it from them first, then unpublish.
                            </p>
                            {conflictRows.length > 0 ? (
                                <div style={{ border: '1px solid #e9ecef', borderRadius: '8px', overflow: 'hidden' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#132848', color: '#ffffff' }}>
                                                <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600 }}>Campaign</th>
                                                <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600 }}>Variant</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {conflictRows.map((r, i) => (
                                                <tr key={i} style={{ backgroundColor: i % 2 === 1 ? '#f8f9fa' : '#ffffff' }}>
                                                    <td style={{ padding: '10px 14px', borderTop: '1px solid #e9ecef', color: '#212529' }}>{r.campaign_name || r.campaign_id || '—'}</td>
                                                    <td style={{ padding: '10px 14px', borderTop: '1px solid #e9ecef', color: '#212529' }}>{r.variant_name || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p style={{ fontSize: '14px', color: '#344054', margin: 0 }}>
                                    This section is in use by a campaign on this landing page.
                                </p>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className='d-flex align-items-center gap-2 mb-2'>
                                <Spinner size='sm' />
                                <strong>{(jobProgress && jobProgress.length > 0 && jobProgress[jobProgress.length - 1]?.message) ? jobProgress[jobProgress.length - 1].message : (jobStatus || 'processing')}</strong>
                            </div>
                            <ProgressBar now={(jobStatus === 'succeeded' || jobStatus === 'failed') ? 100 : Math.max(5, Math.min(95, Math.round(((jobProgress?.length || 1) / 6) * 90) + 5))} label={`${(jobStatus === 'succeeded' || jobStatus === 'failed') ? 100 : Math.max(5, Math.min(95, Math.round(((jobProgress?.length || 1) / 6) * 90) + 5))}%`} animated striped />
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' onClick={() => {
                        if (pollIntervalRef.current) {
                            clearInterval(pollIntervalRef.current);
                            pollIntervalRef.current = null;
                        }
                        setConflicts([]);
                        setShowProgressModal(false);
                    }}>{conflicts.length > 0 ? 'Close' : 'Cancel'}</Button>
                </Modal.Footer>
            </Modal>

            {/* Unpublish (unsave) — pick landing page Modal */}
            <Modal show={showUnsaveModal} onHide={() => { if (!unsaveBusy) setShowUnsaveModal(false); }} centered>
                <Modal.Header
                    closeButton={!unsaveBusy}
                    closeVariant="white"
                    style={{ backgroundColor: '#132848', color: '#ffffff', borderBottom: 'none', padding: '16px 24px' }}
                >
                    <Modal.Title style={{ color: '#ffffff', fontWeight: '600', fontSize: '18px' }}>Unpublish Section</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '24px' }}>
                    <p style={{ fontSize: '14px', marginBottom: '12px' }}>
                        Remove <strong>{unsaveSection?.section_name || unsaveSection?.section_id}</strong> from a landing page:
                    </p>
                    <Form.Group>
                        <Form.Label style={{ color: '#132848', fontWeight: '600', fontSize: '14px' }}>Landing Page</Form.Label>
                        <Form.Select
                            value={unsaveSelectedLp}
                            onChange={(e) => setUnsaveSelectedLp(e.target.value)}
                            disabled={unsaveBusy}
                            style={{ borderColor: '#dee2e6', borderRadius: '6px', fontSize: '14px' }}
                        >
                            {unsaveLpOptions.map((lp, i) => <option key={i} value={lp}>{lp}</option>)}
                        </Form.Select>
                        <p style={{ fontSize: '12px', color: '#6c757d', margin: '8px 0 0 0' }}>
                            Removes it from the chosen landing page only. Blocked if a campaign on that LP uses it.
                        </p>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #e9ecef', padding: '16px 24px', gap: '8px' }}>
                    <Button
                        onClick={() => setShowUnsaveModal(false)}
                        disabled={unsaveBusy}
                        style={{ padding: '8px 20px', fontWeight: '500', borderRadius: '6px', fontSize: '14px', color: '#495057', backgroundColor: '#ffffff', border: '1px solid #dee2e6' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={submitUnsave}
                        disabled={!unsaveSelectedLp || unsaveBusy}
                        style={{ padding: '8px 20px', fontWeight: '500', borderRadius: '6px', fontSize: '14px', color: '#ffffff', backgroundColor: (!unsaveSelectedLp || unsaveBusy) ? '#6c8aaa' : '#dc3545', border: 'none' }}
                    >
                        {unsaveBusy ? <><Spinner size="sm" className="me-1" />Working...</> : 'Unpublish'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default SavedSections;
